/** Change photo… (plan "Avatar"): the avatar stays outside the changeset — this dialog runs
 *  the users PATCH (multipart) on its own. Choose a file to see it before saving; Remove
 *  asks first (the app and the dashboard then show the initial). A failure stays in the
 *  dialog, in words. */
import { Alert, Avatar, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as usersApi from '@/api/users';

import { userInitial } from '../UserRecordHeader';

export function AvatarDialog({
  user,
  onClose,
  onSaved,
}: {
  user: usersApi.ConsoleUserDetail;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const titleId = useId();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview],
  );
  const fail = (e: unknown, what: string) => setError(e instanceof ConsoleApiError ? e.message : `Could not ${what}.`);
  const upload = useMutation({
    mutationFn: (f: File) => usersApi.patchWithAvatar(user.id, f),
    onSuccess: () => onSaved('Photo updated.'),
    onError: (e) => fail(e, 'save the photo'),
  });
  const remove = useMutation({
    mutationFn: () => usersApi.patch(user.id, { avatar: null }),
    onSuccess: () => onSaved('Photo removed.'),
    onError: (e) => fail(e, 'remove the photo'),
  });
  const busy = upload.isPending || remove.isPending;
  const shown = preview ?? user.avatar?.medium ?? undefined;

  return (
    <Dialog open onClose={busy ? undefined : onClose} aria-labelledby={titleId} maxWidth="xs" fullWidth>
      <DialogTitle id={titleId}>Change photo</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, justifyItems: 'center' }}>
        <Avatar src={shown} alt="" sx={{ width: 96, height: 96, fontSize: 36 }} data-avatar-preview>
          {userInitial(user)}
        </Avatar>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          data-avatar-file
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            setError(null);
            setConfirmRemove(false);
            setFile(f);
            setPreview(f ? URL.createObjectURL(f) : null);
          }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => fileRef.current?.click()} disabled={busy}>
            {user.avatar || file ? 'Choose another…' : 'Choose a photo…'}
          </Button>
          {user.avatar && !file && (
            <Button color="error" size="small" onClick={() => setConfirmRemove(true)} disabled={busy}>
              Remove
            </Button>
          )}
        </Box>
        {confirmRemove && (
          <Alert
            severity="warning"
            sx={{ width: '100%' }}
            action={
              <Button color="inherit" size="small" onClick={() => remove.mutate()} disabled={busy}>
                Remove photo
              </Button>
            }
          >
            Remove this photo? The app and the dashboard show the initial instead.
          </Alert>
        )}
        {file && (
          <Typography variant="caption" color="text.secondary">
            {file.name} — not saved yet
          </Typography>
        )}
        {error && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!file || busy} onClick={() => file && upload.mutate(file)}>
          {upload.isPending ? 'Saving…' : 'Save photo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
